/**
 * MemoryPanelView — renders inside the 320px RightPanel when the user toggles
 * to "Memory" view. Shows real-time extracted memories from the current session
 * with Save/Dismiss actions, and previously saved STM items for context.
 */
import { Brain, Bookmark, X, Sparkles, FileText, ListChecks, Link2, Heart } from 'lucide-react';
import { useMemoryStreamStore, type ExtractedMemoryItem } from '@/stores/memoryStream.store';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createMemory, type MemoryKind } from '@/api/services/memory.service';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CATEGORY_ICON: Record<string, typeof Brain> = {
  fact: FileText,
  preference: Heart,
  decision: Sparkles,
  todo: ListChecks,
  reference: Link2,
  context: Brain,
};

function ConfidenceDot({ confidence }: { confidence: number }): JSX.Element {
  const color =
    confidence >= 0.8
      ? 'bg-wayne-green'
      : confidence >= 0.5
        ? 'bg-yellow-400'
        : 'bg-wayne-ink-3';

  return (
    <span
      aria-label={`Confidence: ${Math.round(confidence * 100)}%`}
      className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${color}`}
    />
  );
}

// ---------------------------------------------------------------------------
// Item row
// ---------------------------------------------------------------------------

interface MemoryItemRowProps {
  readonly item: ExtractedMemoryItem;
  readonly onSave: (item: ExtractedMemoryItem) => void;
  readonly onDismiss: (memoryId: string) => void;
  readonly saving: boolean;
}

function MemoryItemRow({ item, onSave, onDismiss, saving }: MemoryItemRowProps): JSX.Element {
  const Icon = CATEGORY_ICON[item.category] ?? Brain;

  return (
    <li
      className="flex items-start gap-2 p-2 rounded-lg border border-wayne-hairline bg-wayne-surface animate-[cardEnter_200ms_ease-out_both]"
      data-testid={`memory-item-${item.memoryId}`}
    >
      <Icon size={14} className="text-wayne-ink-3 shrink-0 mt-0.5" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="text-[12px] text-wayne-ink leading-snug m-0 break-words">
          {item.content}
        </p>
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-[10px] text-wayne-ink-3 uppercase font-medium">
            {item.category}
          </span>
          <ConfidenceDot confidence={item.confidence} />
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {item.saved ? (
          <span className="text-[10px] text-wayne-green font-medium px-1">Saved</span>
        ) : (
          <button
            type="button"
            onClick={() => onSave(item)}
            disabled={saving}
            className="flex items-center justify-center w-5 h-5 rounded text-wayne-ink-3 hover:text-wayne-accent hover:bg-wayne-accent/10 transition-colors disabled:opacity-40"
            aria-label={`Save memory: ${item.content.slice(0, 30)}`}
          >
            <Bookmark size={12} />
          </button>
        )}
        <button
          type="button"
          onClick={() => onDismiss(item.memoryId)}
          className="flex items-center justify-center w-5 h-5 rounded text-wayne-ink-3 hover:text-wayne-ink hover:bg-wayne-surface-2 transition-colors"
          aria-label={`Dismiss memory: ${item.content.slice(0, 30)}`}
        >
          <X size={12} />
        </button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function MemoryPanelView(): JSX.Element {
  const items = useMemoryStreamStore((s) => s.items);
  const markSaved = useMemoryStreamStore((s) => s.markSaved);
  const dismissItem = useMemoryStreamStore((s) => s.dismissItem);
  const queryClient = useQueryClient();

  const saveMutation = useMutation({
    mutationFn: (item: { memoryId: string; content: string; kind: MemoryKind }) =>
      createMemory({ content: item.content, kind: item.kind }),
    onSuccess: (_, variables) => {
      markSaved(variables.memoryId);
      queryClient.invalidateQueries({ queryKey: ['memory'] });
    },
  });

  const categoryToKind = (category: string): MemoryKind => {
    const valid: MemoryKind[] = ['fact', 'preference', 'decision', 'todo', 'reference', 'context'];
    return valid.includes(category as MemoryKind) ? (category as MemoryKind) : 'fact';
  };

  const handleSave = (item: ExtractedMemoryItem): void => {
    saveMutation.mutate({
      memoryId: item.memoryId,
      content: item.content,
      kind: categoryToKind(item.category),
    });
  };

  const handleDismiss = (memoryId: string): void => {
    dismissItem(memoryId);
  };

  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Brain size={14} className="text-wayne-accent" aria-hidden="true" />
        <span
          className="text-wayne-ink-3 font-medium uppercase"
          style={{ fontSize: 11, letterSpacing: '0.08em' }}
        >
          Active Memory
        </span>
        {items.length > 0 ? (
          <span className="relative flex h-2 w-2 ml-1">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-wayne-green opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-wayne-green" />
          </span>
        ) : null}
      </div>

      {/* Item list */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {items.length === 0 ? (
          <p
            className="text-wayne-ink-3 italic m-0"
            style={{ fontSize: 12 }}
            data-testid="memory-panel-empty"
          >
            No memories extracted yet. Start chatting.
          </p>
        ) : (
          <ul className="flex flex-col gap-2 list-none m-0 p-0">
            {items.map((item) => (
              <MemoryItemRow
                key={item.memoryId}
                item={item}
                onSave={handleSave}
                onDismiss={handleDismiss}
                saving={saveMutation.isPending && saveMutation.variables?.memoryId === item.memoryId}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
